import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ThumbDownAltRoundedIcon from '@mui/icons-material/ThumbDownAltRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import MathVisual, { supportsMathVisual } from '../../mini-tasks/components/MathVisual';
import RoundView from '../../mini-tasks/components/RoundView';
import { playMiniTaskSound } from '../../mini-tasks/soundEffects';
import { STAFF_LISTENING_CONTRACT } from '../../mini-tasks/listening/listeningApi';
import PatternRecognitionRoundView, { PatternRecognitionQaView } from '../../mini-tasks/pattern-recognition/PatternRecognitionRoundView';
import { getMeCached } from '../authCache';
import { fetchMiniTaskContentQaCatalog, fetchMiniTaskContentQaQuestions, saveMiniTaskContentQaReview } from '../api/miniTasks';
import TurkishL1QaPanel from './TurkishL1QaPanel';
import CurriculumQaLabels from './CurriculumQaLabels';
import SchoolLifeReviewStatus from './SchoolLifeReviewStatus';
import { turkishTourPreviewLink } from './turkishTourPreviewLink';

const ISSUE_TYPES = [
  ['wrong_answer', 'Cevap hatalı'],
  ['unclear_visual', 'Görsel belirsiz'],
  ['level_fit', 'Seviyeye uygun değil'],
  ['wording', 'Metin / ifade'],
  ['other', 'Diğer'],
];

// Math visual regression tests use this small fixture. The QA journey itself
// renders the real student game components below.
export function QuestionPreview({ question, showAnswer }) {
  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>{question.prompt}</Typography>
      {question.stimulus && !question.audio_required && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{question.stimulus}</Typography>}
      {question.missing_assets?.length > 0 && <Alert severity="warning">Bu soru öğrenci gösterimine hazır değil. Eksik: {question.missing_assets.join(', ')}.</Alert>}
      {question.assessment_mode === 'proxy' && <Alert severity="info">Dolaylı ölçüm: ilgili becerinin doğrudan uygulaması değerlendirilmez.</Alert>}
      {question.visual_spec && (supportsMathVisual(question.visual_spec) ? <MathVisual spec={question.visual_spec} /> : <Alert severity="warning">Soru çizimi henüz hazır değil: {question.visual_spec.renderer}</Alert>)}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        {(question.choices || []).map((choice) => <Box key={choice} sx={{ border: '1px solid', borderColor: showAnswer && choice === question.correct_answer ? 'success.main' : 'divider', bgcolor: showAnswer && choice === question.correct_answer ? '#edf7ed' : 'background.paper', borderRadius: 2, p: 1.25, fontWeight: 700 }}>{choice}</Box>)}
      </Box>
    </Stack>
  );
}

export default function MiniTaskContentQaPage() {
  const [schoolId, setSchoolId] = useState('');
  const [contents, setContents] = useState([]);
  const [lessonName, setLessonName] = useState('');
  const [previewId, setPreviewId] = useState('');
  const [level, setLevel] = useState('');
  const [data, setData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const previewQuestionKey = searchParams.get('preview_question_id');
  const isQuestionPreview = Boolean(previewQuestionKey);
  const directTourPreview = useMemo(() => turkishTourPreviewLink(searchParams), [searchParams]);
  const leaveTourPreview = () => {
    if (!directTourPreview) return;
    const next = new URLSearchParams(searchParams);
    ['preview_id', 'preview_level', 'preview_tour'].forEach(key => next.delete(key));
    setSearchParams(next, { replace: true });
  };
  const [previewFeedback, setPreviewFeedback] = useState(null);
  const [previewChoice, setPreviewChoice] = useState(null);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [error, setError] = useState('');
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueType, setIssueType] = useState('wrong_answer');
  const [note, setNote] = useState('');

  const selectedContent = useMemo(() => contents.find((item) => String(item.preview_id) === String(previewId)) || null, [contents, previewId]);
  const lessonNames = useMemo(() => [...new Set(contents.map((item) => item.lesson_name))], [contents]);
  const lessonContents = useMemo(() => contents.filter((item) => item.lesson_name === lessonName), [contents, lessonName]);
  const qaLevels = useMemo(() => selectedContent?.qa_levels || [], [selectedContent]);
  const activeQuestion = data?.questions?.[currentIndex] || null;
  const completed = started && Boolean(data) && !activeQuestion;
  const relatedOutcomes = useMemo(() => {
    const mapped = Array.isArray(activeQuestion?.outcomes) ? activeQuestion.outcomes : [];
    const canonical = mapped
      .map((item) => ({ code: String(item?.code || '').trim(), text: String(item?.text || '').trim() }))
      .filter((item) => item.code || item.text);
    if (canonical.length) return canonical;
    const metadata = activeQuestion?.metadata || {};
    const rawCodes = metadata.outcome_codes;
    const codes = Array.isArray(rawCodes)
      ? rawCodes
      : String(rawCodes || '').split(/[;,]/);
    const text = String(metadata.focus || metadata.raw_outcome || '').trim();
    return [...new Set(codes.map((item) => String(item).trim()).filter(Boolean))]
      .map((code) => ({ code, text }));
  }, [activeQuestion]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        const sid = me?.data?.admin_user?.school_id;
        if (!sid) throw new Error('Okul bilgisi bulunamadı.');
        const payload = await fetchMiniTaskContentQaCatalog(sid);
        if (cancelled) return;
        const rows = payload.contents || [];
        setSchoolId(String(sid));
        setContents(rows);
        setLessonName(rows[0]?.lesson_name || '');
        const requestedContent = isQuestionPreview
          ? rows.find((item) => String(item.preview_id) === searchParams.get('preview_id'))
          : rows[0];
        if (isQuestionPreview && !requestedContent) throw new Error('Ön izlenecek içerik bulunamadı.');
        setPreviewId(requestedContent?.preview_id ? String(requestedContent.preview_id) : '');
        if (isQuestionPreview) {
          const questionsPayload = await fetchMiniTaskContentQaQuestions(sid, requestedContent.preview_id, {
            level: searchParams.get('preview_level'), onlyUnreviewed: false,
          });
          if (cancelled) return;
          const question = questionsPayload.questions?.find((item) => String(item.key) === previewQuestionKey);
          if (!question) throw new Error('Ön izlenecek soru bulunamadı.');
          setData({ ...questionsPayload, question_count: 1, questions: [{ ...question, position: 1 }] });
          setStarted(true);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'İçerik Değerlendirme ekranı yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isQuestionPreview, previewQuestionKey, searchParams]);

  useEffect(() => {
    if (level && !qaLevels.some((item) => String(item.level) === String(level))) setLevel('');
  }, [level, qaLevels]);

  const handleLessonChange = (nextLesson) => {
    setLessonName(nextLesson);
    const firstTopic = contents.find((item) => item.lesson_name === nextLesson);
    setPreviewId(firstTopic?.preview_id ? String(firstTopic.preview_id) : '');
    setLevel('');
  };

  const startReview = async () => {
    if (!schoolId || !previewId || loading) return;
    setLoading(true);
    setError('');
    try {
      const payload = await fetchMiniTaskContentQaQuestions(schoolId, previewId, {
        level: level || undefined,
        onlyUnreviewed: !showAllQuestions,
      });
      setData(payload);
      setCurrentIndex(0);
      setStarted(true);
    } catch (err) {
      setError(err?.message || 'QA soruları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const leaveReview = () => {
    setStarted(false);
    setData(null);
    setCurrentIndex(0);
    setIssueDialogOpen(false);
    setError('');
  };

  const openPreview = () => {
    if (!activeQuestion) return;
    const params = new URLSearchParams({
      preview_id: previewId,
      preview_level: String(activeQuestion.qa_level),
      preview_question_id: String(activeQuestion.key),
    });
    window.open(`${window.location.pathname}?${params}`, '_blank', 'noopener,noreferrer');
  };

  const answerPreview = (choice) => {
    if (previewFeedback) return;
    const tokenOrder = activeQuestion.qa_correct_token_order;
    const sequence = activeQuestion.answer_mode === 'sequence_builder';
    const correct = sequence
      ? JSON.stringify(choice?.selectedTokenIds) === JSON.stringify(tokenOrder)
      : String(choice) === String(activeQuestion.correct_answer);
    setPreviewChoice(choice);
    playMiniTaskSound(correct ? 'correct' : 'wrong', soundEnabled);
    setPreviewFeedback({
      type: correct ? 'correct' : 'near',
      message: correct ? 'Harika, doğru!' : `Neredeyse! Doğru cevap ${activeQuestion.correct_answer}.`,
      ...(sequence ? { correct_token_order: tokenOrder } : {}),
    });
  };

  const saveReview = async (status) => {
    if (!activeQuestion || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = await saveMiniTaskContentQaReview(schoolId, previewId, activeQuestion.qa_level, {
        question_key: activeQuestion.key,
        status,
        issue_type: status === 'issue' ? issueType : null,
        note: status === 'issue' ? note : null,
      });
      setData((current) => {
        if (!current) return current;
        const questions = current.questions.map((item) => (item.key === activeQuestion.key && item.qa_level === activeQuestion.qa_level ? { ...item, review: payload.review } : item));
        return { ...current, questions, approved_count: questions.filter((item) => item.review?.status === 'approved').length, issue_count: questions.filter((item) => item.review?.status === 'issue').length };
      });
      setIssueDialogOpen(false);
      setCurrentIndex((index) => index + 1);
    } catch (err) {
      setError(err?.message || 'Değerlendirme kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const qaRound = useMemo(() => {
    if (!activeQuestion || !selectedContent || !data) return null;
    return {
      preview: true,
      round_id: `content-qa-${previewId}-${activeQuestion.qa_level}-${activeQuestion.key}`,
      lesson: { code: selectedContent.lesson_code, name: selectedContent.lesson_name },
      skill_key: selectedContent.skill_key,
      task_type: data.task_type,
      question_count: data.question_count,
      duration_seconds: null,
      student: { display_name: 'Öğretmen' },
      adaptive_review: { is_review: false },
    };
  }, [activeQuestion, data, previewId, selectedContent]);

  if (loading && !started) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  if (isQuestionPreview && !started) return <Box sx={{ p: 4 }}><Alert severity="error">{error || 'Ön izleme yüklenemedi.'}</Alert></Box>;

  if (!started) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>İçerik Değerlendirme</Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>Dersi ve konuyu seçin. Level boş kalırsa tüm kaynak level soruları sırayla açılır; tekrar turları değerlendirilmez.</Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <TurkishL1QaPanel schoolId={schoolId} directPreview={directTourPreview} onExitPreview={leaveTourPreview} />
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Stack spacing={2}>
            <TextField select label="Ders / alan" value={lessonName} onChange={(event) => handleLessonChange(event.target.value)}>{lessonNames.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</TextField>
            <TextField select label="Konu / beceri" value={previewId} onChange={(event) => { setPreviewId(event.target.value); setLevel(''); }}>{lessonContents.map((item) => <MenuItem key={item.preview_id} value={String(item.preview_id)}>{item.skill_name}</MenuItem>)}</TextField>
            <TextField select label="Kaynak level (opsiyonel)" value={level} onChange={(event) => setLevel(event.target.value)} helperText="Boş bırakılırsa bütün kaynak level'lar sırayla incelenir."><MenuItem value="">Tüm kaynak level'lar</MenuItem>{qaLevels.map((item) => <MenuItem key={item.level} value={String(item.level)}>Level {item.source_level}</MenuItem>)}</TextField>
            <FormControlLabel control={<Switch checked={showAllQuestions} onChange={(event) => setShowAllQuestions(event.target.checked)} />} label="Tüm sorular" />
            <Button variant="contained" size="large" onClick={startReview} disabled={!previewId || loading} sx={{ alignSelf: 'flex-start' }}>Soruları incelemeye başla</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  if (completed) {
    return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: '#fffaf0' }}><Paper sx={{ maxWidth: 540, p: 4, textAlign: 'center', borderRadius: 4 }}><Typography variant="h4" sx={{ fontWeight: 900 }}>Bu inceleme tamamlandı</Typography><Typography sx={{ mt: 1, color: 'text.secondary' }}>{data.question_count} soru değerlendirildi: {data.approved_count} beğenildi, {data.issue_count} sorun olarak kaydedildi.</Typography><Button sx={{ mt: 3 }} variant="contained" onClick={leaveReview}>Yeni seçim yap</Button></Paper></Box>;
  }

  if (isQuestionPreview) {
    const round = { ...qaRound, round_id: `${qaRound.round_id}-preview-${previewAttempt}`, questions: [activeQuestion.question], duration_seconds: activeQuestion.kind === 'pattern_recognition' ? 120 : null };
    const staffListeningPreview = activeQuestion.listening?.contract === STAFF_LISTENING_CONTRACT;
    const restart = () => { setPreviewFeedback(null); setPreviewChoice(null); setPreviewAttempt((value) => value + 1); };
    return <>
      {activeQuestion.kind === 'pattern_recognition' && previewFeedback
        ? <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#fffaf0' }}><Typography variant="h4">Ön izleme tamamlandı.</Typography></Box>
        : activeQuestion.kind === 'pattern_recognition'
        ? <PatternRecognitionRoundView key={round.round_id} round={round} boardSessionId={null} classTitle="Ön izleme" soundEnabled={soundEnabled} onToggleSound={() => setSoundEnabled((value) => !value)} onExit={() => window.close()} onComplete={() => setPreviewFeedback({ type: 'correct', message: 'Ön izleme tamamlandı.' })} />
        : <RoundView key={round.round_id} round={round} question={activeQuestion} feedback={previewFeedback} submittingChoice={null} selectedChoice={previewChoice} correctAnswer={previewFeedback ? activeQuestion.correct_answer : null} classTitle="Ön izleme" soundEnabled={soundEnabled} onToggleSound={() => setSoundEnabled((value) => !value)} onBackToBoard={() => window.close()} onAnswer={answerPreview} boardSessionId={null} staffSchoolId={schoolId} onTimeout={() => {}} />}
      <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 25,
        ...(staffListeningPreview ? {
          // Audio and picture choices can make the mobile card taller than the
          // viewport. Keep restart after the card so it cannot cover a choice.
          position: { xs: 'relative', sm: 'fixed' }, bottom: { xs: 'auto', sm: 16 },
          left: { xs: 'auto', sm: '50%' }, transform: { xs: 'none', sm: 'translateX(-50%)' },
          my: { xs: 2, sm: 0 }, display: 'flex', justifyContent: 'center',
        } : {}),
      }}><Button variant="contained" onClick={restart} sx={staffListeningPreview ? { minHeight: 48 } : undefined}>Yeniden dene</Button></Box>
    </>;
  }

  return (
    <>
      {activeQuestion?.kind === 'pattern_recognition' ? <PatternRecognitionQaView question={activeQuestion.question} classTitle="İçerik Değerlendirme" position={activeQuestion.position} questionCount={data.question_count} onExit={leaveReview} /> : <RoundView round={qaRound} question={activeQuestion} feedback={null} submittingChoice={null} selectedChoice={activeQuestion?.correct_answer} correctAnswer={activeQuestion?.correct_answer} classTitle="İçerik Değerlendirme" soundEnabled={false} onToggleSound={() => {}} onBackToBoard={leaveReview} onAnswer={() => {}} boardSessionId={null} staffSchoolId={schoolId} onTimeout={() => {}} qaMode answerRevealed hideFeedbackPanel />}
      {error ? <Alert severity="error" sx={{ position: 'fixed', zIndex: 30, top: 16, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 32px)' }}>{error}</Alert> : null}
      <Paper elevation={6} sx={{ position: 'fixed', zIndex: 25, left: '50%', bottom: { xs: 16, sm: 24 }, transform: 'translateX(-50%)', width: 'min(920px, calc(100vw - 28px))', p: 1.25, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.97)',
        ...(activeQuestion?.staff_review ? {
          // Keep every option readable on narrow screens. The real game view
          // retains its layout; the staff actions follow it in document flow.
          position: { xs: 'relative', sm: 'fixed' },
          left: { xs: 'auto', sm: '50%' }, bottom: { xs: 'auto', sm: 24 },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          mx: { xs: 'auto', sm: 0 }, my: { xs: 2, sm: 0 },
          '& .MuiButton-root:focus-visible': { outline: '3px solid #4c1d95', outlineOffset: 3 },
        } : {}),
      }}>
        <SchoolLifeReviewStatus question={activeQuestion} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Typography sx={{ fontWeight: 900, color: 'text.secondary', whiteSpace: 'nowrap' }}>Level {activeQuestion?.source_level}</Typography>
          <CurriculumQaLabels question={activeQuestion} legacyOutcomes={relatedOutcomes} />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={openPreview} disabled={saving} sx={{ borderRadius: 99, whiteSpace: 'nowrap', flexShrink: 0 }}>Ön izleme</Button>
            <Button aria-label="Soruyu beğendim" title="Beğendim" variant="contained" color="success" onClick={() => saveReview('approved')} disabled={saving} sx={{ borderRadius: 99, minWidth: 48, width: 48, height: 48, p: 0 }}>{saving ? <CircularProgress size={22} color="inherit" /> : <ThumbUpAltRoundedIcon />}</Button>
            <Button aria-label="Soruda sorun var" title="Beğenmedim" variant="contained" color="warning" onClick={() => { setIssueType(activeQuestion?.review?.issue_type || 'wrong_answer'); setNote(activeQuestion?.review?.note || ''); setIssueDialogOpen(true); }} disabled={saving} sx={{ borderRadius: 99, minWidth: 48, width: 48, height: 48, p: 0 }}><ThumbDownAltRoundedIcon /></Button>
          </Stack>
        </Stack>
      </Paper>
      <Dialog open={issueDialogOpen} onClose={() => !saving && setIssueDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Sorunu hızlıca not edin</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}><TextField select label="Sorun türü" value={issueType} onChange={(event) => setIssueType(event.target.value)} fullWidth>{ISSUE_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField><TextField label="Kısa not (isteğe bağlı)" value={note} onChange={(event) => setNote(event.target.value)} fullWidth multiline minRows={2} maxRows={4} autoFocus /></Stack></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setIssueDialogOpen(false)} disabled={saving}>Vazgeç</Button><Button variant="contained" color="warning" onClick={() => saveReview('issue')} disabled={saving}>Kaydet ve sonraki soruya geç</Button></DialogActions>
      </Dialog>
    </>
  );
}
